import { z } from 'zod'
import { api } from '@/shared/api/client'
import { uuidSchema } from '@/shared/api/schemas'

export const CUSTOMER_TYPES = ['VIP', 'ORDINARY'] as const
export type CustomerType = (typeof CUSTOMER_TYPES)[number]

export const DEVICE_OWNERSHIPS = ['PURCHASED', 'RENTED'] as const
export type DeviceOwnership = (typeof DEVICE_OWNERSHIPS)[number]

export type AttachmentKind = 'PHOTO' | 'CONTRACT'

export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024

export const subscriberProfileSchema = z.object({
  customerId: z.string(),
  firstName: z.string().nullish(),
  fatherName: z.string().nullish(),
  lastName: z.string().nullish(),
  deviceTypeId: uuidSchema.nullish(),
  deviceTypeName: z.string().nullish(),
  deviceOwnership: z.enum(DEVICE_OWNERSHIPS).nullish(),
  customerType: z.enum(CUSTOMER_TYPES).nullish(),
  whatsapp: z.string().nullish(),
  photoFileId: uuidSchema.nullish(),
  contractFileId: uuidSchema.nullish(),
  installedByPersonId: uuidSchema.nullish(),
  installedByName: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
})

export type SubscriberProfile = z.infer<typeof subscriberProfileSchema>

export interface SubscriberProfileDraft {
  firstName?: string | null
  fatherName?: string | null
  lastName?: string | null
  deviceTypeId?: string | null
  deviceOwnership?: DeviceOwnership | null
  customerType?: CustomerType | null
  whatsapp?: string | null
  photoFileId?: string | null
  contractFileId?: string | null
  installedByPersonId?: string | null
  installedByName?: string | null
  latitude?: number | null
  longitude?: number | null
}

export const PROFILE_REQUIRED = [
  'firstName',
  'fatherName',
  'lastName',
  'deviceTypeId',
  'deviceOwnership',
  'customerType',
  'whatsapp',
  'photoFileId',
  'contractFileId',
  'installedByName',
  'latitude',
] as const satisfies readonly (keyof SubscriberProfile)[]

export function missingProfileFields(p: SubscriberProfile | null | undefined): string[] {
  if (!p) return [...PROFILE_REQUIRED]
  return PROFILE_REQUIRED.filter((key) => p[key] == null || p[key] === '')
}

const deviceTypeSchema = z.object({ id: uuidSchema, name: z.string(), active: z.boolean() })
export type DeviceType = z.infer<typeof deviceTypeSchema>

export async function fetchDeviceTypes(includeInactive = false): Promise<DeviceType[]> {
  const response = await api.get<unknown>('/api/subscribers/device-types', {
    params: { includeInactive },
  })
  return z.array(deviceTypeSchema).parse(response.data)
}

export async function saveDeviceType(
  id: string | null,
  body: { name: string; active: boolean },
): Promise<DeviceType> {
  const response = id
    ? await api.put<unknown>(`/api/subscribers/device-types/${id}`, body)
    : await api.post<unknown>('/api/subscribers/device-types', body)
  return deviceTypeSchema.parse(response.data)
}

const attachmentSchema = z.object({
  id: uuidSchema,
  contentType: z.string().nullish(),
  sizeBytes: z.number(),
  originalName: z.string().nullish(),
})
export type Attachment = z.infer<typeof attachmentSchema>

export async function uploadAttachment(file: File, kind: AttachmentKind): Promise<Attachment> {
  const form = new FormData()
  form.append('file', file)
  form.append('kind', kind)
  const response = await api.post<unknown>('/api/subscribers/attachments', form, {
    headers: { 'Content-Type': undefined },
  })
  return attachmentSchema.parse(response.data)
}

export async function attachmentUrl(subscriberId: string, kind: AttachmentKind): Promise<string> {
  const response = await api.get<unknown>(
    `/api/subscribers/${subscriberId}/attachments/${kind}/url`,
  )
  return z.object({ url: z.string() }).parse(response.data).url
}

const installerSchema = z.object({
  id: uuidSchema,
  fullName: z.string(),
  personnelNo: z.string().nullish(),
  jobTitleName: z.string().nullish(),
  orgNodeName: z.string().nullish(),
})
export type Installer = z.infer<typeof installerSchema>

export async function searchInstallers(q: string, signal?: AbortSignal): Promise<Installer[]> {
  const term = q.trim()
  const response = await api.get<unknown>('/api/subscribers/installers', {
    signal,
    params: term ? { q: term } : {},
  })
  return z.array(installerSchema).parse(response.data)
}

export function googleMapsLink(lat: number, lng: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
}
